import {
  CARMAX_EXCLUDED_LIENHOLDERS,
  CARMAX_LEASE_POLICY_URL,
  CARVANA_LEASE_POLICY_URL,
  Competitor,
  CompetitorBucket,
  getCompetitorBucket,
} from '../../../../../../constants/competitorLeasePolicies';
import { LienholderSlugEnum } from '../../../../../../gql/generated/graphql';
import { indefiniteArticle } from '../../../../../../utils/strings';

type CompetitorPolicyLink = {
  competitor: Competitor;
  label: string;
  href: string;
};

type CompetitorPolicyParagraph = {
  text: string;
  bullets?: string[];
};

export type CompetitorTruth = {
  bucket: CompetitorBucket;
  sentence: string;
  heading: string;
  body: CompetitorPolicyParagraph[];
  links: CompetitorPolicyLink[];
};

const withTextFragment = (url: string, text: string): string =>
  `${url}#:~:text=${encodeURIComponent(text)}`;

const CLOSER = "At Lease End, that's not a problem.";

const CARVANA_STEPS = [
  'Buy out your lease yourself',
  'Retitle the car in your name',
  'Then sell it to them',
];

const carvanaBody = (name: string): CompetitorPolicyParagraph => ({
  text: `Carvana only buys out leases from a short list of leasing companies. ${name} is not on it. To sell to Carvana, you would have to:`,
  bullets: CARVANA_STEPS,
});

const carmaxBody = (name: string): CompetitorPolicyParagraph => ({
  text: `CarMax does not buy out ${name} leases at all. Their site lists ${name} as a company they can't purchase leases through.`,
});

// No text fragment: Carvana's page lists approved lenders, so a lender we link for is never on it.
const CARVANA_LINK: CompetitorPolicyLink = {
  competitor: 'carvana',
  label: "Carvana's lease buyout policy",
  href: CARVANA_LEASE_POLICY_URL,
};

const carmaxLink = (slug: string): CompetitorPolicyLink => {
  const anchor = CARMAX_EXCLUDED_LIENHOLDERS[slug as LienholderSlugEnum];
  return {
    competitor: 'carmax',
    label: "CarMax's lease buyout policy",
    href:
      anchor != null ? withTextFragment(CARMAX_LEASE_POLICY_URL, anchor) : CARMAX_LEASE_POLICY_URL,
  };
};

export const getCompetitorTruth = (
  lienholderSlug?: string | null,
  lienholderName?: string | null,
): CompetitorTruth | null => {
  if (!lienholderSlug) return null;
  const bucket = getCompetitorBucket(lienholderSlug);
  if (bucket == null || bucket === 'bothCan' || !lienholderName) return null;

  const leaseClause = `${indefiniteArticle(lienholderName)} ${lienholderName} lease. ${CLOSER}`;

  if (bucket === 'bothCannot') {
    return {
      bucket,
      sentence: `Neither Carvana nor CarMax can buy out ${leaseClause}`,
      heading: 'Straight from their policies',
      body: [carvanaBody(lienholderName), carmaxBody(lienholderName)],
      links: [CARVANA_LINK, carmaxLink(lienholderSlug)],
    };
  }
  if (bucket === 'carvanaCannot') {
    return {
      bucket,
      sentence: `Carvana can't buy out ${leaseClause}`,
      heading: 'Straight from their policy',
      body: [carvanaBody(lienholderName)],
      links: [CARVANA_LINK],
    };
  }
  return {
    bucket,
    sentence: `CarMax can't buy out ${leaseClause}`,
    heading: 'Straight from their policy',
    body: [carmaxBody(lienholderName)],
    links: [carmaxLink(lienholderSlug)],
  };
};
